import type { AdUnit } from './api';

export type ScanStatus = 'idle' | 'loading' | 'loaded' | 'error';

export interface AppRowState {
  status: ScanStatus;
  expanded: boolean;
  adUnits: AdUnit[];
  error?: string;
  /** instanceId -> HTTP status from the write, populated after an update. */
  instanceStatus?: Record<string, number>;
  updateSucceeded?: boolean;
}

const BADGE: Record<ScanStatus, { label: string; className: string }> = {
  idle: { label: 'Not loaded', className: 'bg-gray-100 text-gray-600' },
  loading: { label: 'Loading', className: 'bg-blue-50 text-blue-700' },
  loaded: { label: 'Ready', className: 'bg-green-50 text-green-700' },
  error: { label: 'Failed', className: 'bg-red-50 text-red-700' },
};

function StatusIcon({ ok }: { ok: boolean }) {
  return (
    <svg
      className={`h-5 w-5 ${ok ? 'text-green-500' : 'text-red-500'}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-label={ok ? 'Succeeded' : 'Failed'}
      role="img"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d={ok ? 'M5 13l4 4L19 7' : 'M6 18L18 6M6 6l12 12'}
      />
    </svg>
  );
}

export function AppRow({
  appName,
  state,
  onToggle,
}: {
  appName: string;
  state: AppRowState;
  onToggle: () => void;
}) {
  const badge = state.status === 'loaded' && !state.adUnits.length
    ? { label: 'No ad units', className: 'bg-gray-100 text-gray-600' }
    : BADGE[state.status];

  return (
    <div className="rounded-lg border border-gray-300 bg-white shadow-sm">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={state.expanded}
        className="flex w-full items-center justify-between p-4 text-left hover:bg-gray-50"
      >
        <span className="flex min-w-0 items-center gap-3">
          <span className="truncate text-lg font-bold text-gray-800">{appName}</span>
          <span className={`shrink-0 rounded-full px-2 py-1 text-xs font-semibold ${badge.className}`}>
            {badge.label}
          </span>
          <span className="shrink-0 rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-600">
            {state.adUnits.length}
          </span>
          {state.updateSucceeded !== undefined && <StatusIcon ok={state.updateSucceeded} />}
        </span>
        <svg
          className={`ml-3 h-6 w-6 shrink-0 transition-transform ${state.expanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {state.expanded && (
        <div className="overflow-x-auto border-t border-gray-200 p-4">
          {state.status === 'loading' && (
            <div className="rounded-lg bg-blue-50 p-4 text-sm text-blue-700">Loading ad units…</div>
          )}

          {state.status === 'error' && (
            <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700">
              {state.error ?? 'App scan failed'}
            </div>
          )}

          {state.status === 'idle' && (
            <div className="rounded-lg bg-gray-50 p-4 text-sm text-gray-600">
              Open this app to fetch ad units.
            </div>
          )}

          {state.status === 'loaded' &&
            (state.adUnits.length === 0 ? (
              <div className="rounded-lg bg-gray-50 p-4 text-sm text-gray-600">
                No matching Google Ad Manager ad units were found for this app.
              </div>
            ) : (
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    {['Instance Name', 'Ad Format', 'Existing Ad Unit ID', 'Refresh Ad Unit ID', 'Rate', 'Status'].map(
                      (heading) => (
                        <th
                          key={heading}
                          className="px-4 py-2 text-left text-xs font-medium tracking-wider text-gray-500 uppercase"
                        >
                          {heading}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {state.adUnits.map((unit) => {
                    const written = state.instanceStatus?.[String(unit.instance_id)];
                    return (
                      <tr key={String(unit.instance_id)}>
                        <td className="px-4 py-3 text-sm whitespace-nowrap text-gray-700">{unit.instance_name}</td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap text-gray-700">{unit.ad_format}</td>
                        <td className="px-4 py-3 font-mono text-sm whitespace-nowrap text-gray-700">
                          {unit.previous_ad_unit_id}
                        </td>
                        <td className="px-4 py-3 font-mono text-sm whitespace-nowrap text-gray-700">
                          {unit.new_ad_unit_id}
                        </td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap text-gray-700">{unit.rate ?? 'N/A'}</td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap text-gray-700">
                          {written !== undefined && <StatusIcon ok={written >= 200 && written < 300} />}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ))}
        </div>
      )}
    </div>
  );
}
